import { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  IconButton,
  Snackbar,
  SnackbarContent,
  Typography,
} from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { informationInviteAtom } from 'atoms/inviteSession';
import { useAtom } from 'jotai';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';
import { wellzTheme } from 'theme';
import { formatDate, formatHour } from 'utils/helpers';

import { GetBookingInvites, InviteStatus } from 'services/index';
import { getBookingsInvites } from 'services/specialist/specialist';

import { keyByStatus } from './components/StatusDropdownButton/constant';
import { StatusDropdownButton } from './components/StatusDropdownButton/StatusDropdownButton';
import { Empty } from './Empty';
import { compareExpiration } from './utils';

import { DrawerInvitationHistoryProps } from './types';

import * as S from './styles';

const DrawerInvitationHistory: React.FC<DrawerInvitationHistoryProps> = ({
  onCloseDrawer,
  onBackButton,
}) => {
  const [inviteStatus, setInviteStatus] = useState<InviteStatus | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const intl = useIntl();
  const [ref, inView] = useInView();
  const [informationInvite, setInformationInvite] = useAtom(
    informationInviteAtom,
  );

  const {
    data: bookingInvites,
    fetchNextPage,
    isFetchingNextPage,
    isFetching,
  } = useInfiniteQuery<GetBookingInvites>({
    queryKey: ['bookingInvites', inviteStatus] as const,
    queryFn: ({ pageParam }) => getBookingsInvites(inviteStatus, pageParam),
    getNextPageParam: lastPage => lastPage.metadata.nextCursor,
  });

  useEffect(() => {
    if (inView && bookingInvites) {
      const totalPagesFetcheds = bookingInvites?.pages.length;
      const totalPages = bookingInvites?.pages[0].metadata.totalPages;

      if (totalPagesFetcheds < totalPages) {
        fetchNextPage();
      }
    }
  }, [bookingInvites, fetchNextPage, inView]);

  const handleBackButton = () => {
    if (informationInvite.patientName) {
      return onBackButton();
    }
    return onCloseDrawer();
  };

  const handleCloseDrawer = () => {
    if (informationInvite.patientName) {
      setInformationInvite({
        inviteLink: '',
        patientName: '',
        sessionCreated: '',
      });
    }
    return onCloseDrawer();
  };

  return (
    <S.Container data-testid="drawer-invitation-history">
      <S.Header>
        <IconButton onClick={handleBackButton}>
          <S.ArrowLeftIcon />
        </IconButton>

        <S.Title variant="subtitle2">
          <FormattedMessage id="CPPLATFORM.CPINVITEMANAGEMENT.HEADER" />
        </S.Title>

        <IconButton data-testid="close-drawer" onClick={handleCloseDrawer}>
          <S.CloseIcon />
        </IconButton>
      </S.Header>
      <S.DrawerContent>
        <S.DrawerTitle variant="h5">
          <FormattedMessage id="CPPLATFORM.CPINVITEMANAGEMENT.TITLE" />
        </S.DrawerTitle>
        <S.DrawerSubtitle variant="body1">
          <FormattedMessage id="CPPLATFORM.CPINVITEMANAGEMENT.SUBTITLE" />
        </S.DrawerSubtitle>
        <S.SelectStatusContainer>
          <StatusDropdownButton setStatus={setInviteStatus} />
        </S.SelectStatusContainer>

        {isFetching && (
          <S.LoadingBox>
            <S.Loading size="1rem" />
          </S.LoadingBox>
        )}

        {!bookingInvites?.pages && !isFetching && <Empty />}

        {bookingInvites?.pages && (
          <div>
            {bookingInvites.pages.map(({ results }) =>
              results.map(invitation => {
                const { hours } = compareExpiration(invitation.expired_at);

                return (
                  <S.InvitationItem key={invitation.url}>
                    <S.InvitationRow>
                      <Typography
                        sx={{ color: wellzTheme.colors.text.disabled }}
                        variant="caption"
                      >
                        <FormattedMessage
                          id={
                            !invitation.rescheduled_session
                              ? 'CPPLATFORM.CPINVITEMANAGEMENT.NEWINVITATIONTAG'
                              : 'CPPLATFORM.CPINVITEMANAGEMENT.PROPOSEDTAG'
                          }
                        />
                      </Typography>
                      <S.InvitationExpiry
                        color={keyByStatus[invitation.status].color}
                        variant="caption"
                      >
                        {keyByStatus[invitation.status].icon}
                        <FormattedMessage
                          id={keyByStatus[invitation.status].key}
                          values={{
                            expired_at: `${hours}h`,
                          }}
                        />
                      </S.InvitationExpiry>
                    </S.InvitationRow>
                    <S.InvitationRow style={{ alignItems: 'flex-start' }}>
                      <S.InvitationInfo>
                        <S.InvitationDetails>
                          <S.InvitationName variant="body1">
                            {invitation.end_user_full_name}
                          </S.InvitationName>
                          <S.InvitationDate>
                            <S.TextRow variant="body2">
                              <S.IconCalendar />
                              {formatDate(invitation.slot_date, {
                                locale: intl.locale,
                              })}
                            </S.TextRow>
                            <S.TextRow variant="body2">
                              <S.IconAccessTime />
                              {formatHour(invitation.slot_date, {
                                locale: intl.locale,
                                omitMinutesWhenZero: false,
                              })}
                            </S.TextRow>
                          </S.InvitationDate>
                        </S.InvitationDetails>
                      </S.InvitationInfo>
                      <S.CopyLinkButton
                        onClick={() => {
                          navigator.clipboard.writeText(invitation.url);
                          setOpenSnackbar(true);
                        }}
                        variant="text"
                        color="primary"
                        size="small"
                        disabled={invitation.status !== 'pending'}
                      >
                        <S.IconLink
                          disabled={invitation.status !== 'pending'}
                        />
                        <FormattedMessage id="CPPLATFORM.CPINVITEMANAGEMENT.COPYLINKBUTTON" />
                      </S.CopyLinkButton>
                    </S.InvitationRow>
                  </S.InvitationItem>
                );
              }),
            )}

            <S.ScrollToMeForRefetch ref={ref}>
              {isFetchingNextPage && <S.Loading size="1rem" />}
            </S.ScrollToMeForRefetch>
          </div>
        )}
        <Snackbar
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={() => {
            setOpenSnackbar(false);
          }}
        >
          <SnackbarContent
            message={intl.formatMessage({
              id: 'CPPLATFORM.CPINVITECONFIRM.LINKCOPIED',
            })}
            sx={{ backgroundColor: wellzTheme.colors.grade.low }}
            action={(() => (
              <IconButton
                aria-label="close"
                sx={{ p: 0.5 }}
                color="inherit"
                onClick={() => {
                  setOpenSnackbar(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            ))()}
          />
        </Snackbar>
      </S.DrawerContent>
    </S.Container>
  );
};

export default DrawerInvitationHistory;
